import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/firebaseConfig';

// Create the context so any component can access auth data
const AuthContext = createContext();

// Custom hook - components call useAuth() instead of useContext(AuthContext)
export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true); // don't render until Firebase is ready

  // Create a new account and save user info to Firestore
  async function signup(email, password, name) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    // Store extra info in Firestore (Firebase Auth only holds email/password)
    await setDoc(doc(db, 'users', result.user.uid), {
      name: name,
      email: email,
      isAdmin: false,
      createdAt: new Date()
    });
    return result;
  }

  // Log in with email and password
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Log out the current user
  function logout() {
    return signOut(auth);
  }

  // This runs once when the app loads and listens for login/logout events
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Check Firestore to see if this user is an admin
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setIsAdmin(userDoc.data().isAdmin === true);
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });

    // Cleanup listener when component unmounts
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    isAdmin,
    signup,
    login,
    logout
  };

  // Don't render children until Firebase auth has loaded
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
